import { Link, useLocation } from "react-router-dom";
import { useSeo } from "../lib/seo";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";

const I18N = {
  en: {
    overline: "Privacy",
    title: "Privacy Policy",
    subtitle: "How we process your data",
    intro: "When you submit an inquiry via our contact form, we process the personal data you provide solely to respond to and contact you.",
    bullets: [
      "When you submit an inquiry via our contact form, we process the personal data you provide (such as name, surname, email address, and phone number) solely for the purpose of processing your request and contacting you.",
      "The legal basis for this processing is Article 6(1)(a) GDPR (consent) and/or Article 6(1)(b) GDPR where the inquiry relates to pre-contractual measures.",
      "Your data will not be shared with third parties unless required by law or necessary for the fulfillment of your request.",
      "The data will be stored only for as long as necessary to process your inquiry or to comply with statutory retention obligations.",
      "You have the right to request access to your personal data, rectification, deletion, restriction of processing, data portability, and to withdraw your consent at any time with future effect.",
    ],
    controller: "Controller",
    backHome: "← Back to home",
    updated: "Last updated",
  },
  de: {
    overline: "Datenschutz",
    title: "Datenschutzerklärung",
    subtitle: "Wie wir Ihre Daten verarbeiten",
    intro: "Wenn Sie eine Anfrage über unser Kontaktformular senden, verarbeiten wir die von Ihnen angegebenen personenbezogenen Daten ausschließlich, um Ihre Anfrage zu bearbeiten und Sie zu kontaktieren.",
    bullets: [
      "Wenn Sie eine Anfrage über unser Kontaktformular senden, verarbeiten wir die von Ihnen angegebenen personenbezogenen Daten (insbesondere Name, Nachname, E-Mail-Adresse und Telefonnummer) ausschließlich zum Zweck der Bearbeitung Ihrer Anfrage und der Kontaktaufnahme.",
      "Rechtsgrundlage der Verarbeitung ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) und/oder Art. 6 Abs. 1 lit. b DSGVO, sofern die Anfrage vorvertraglichen Maßnahmen dient.",
      "Ihre Daten werden nicht an Dritte weitergegeben, sofern dies nicht gesetzlich vorgeschrieben oder zur Erfüllung Ihrer Anfrage erforderlich ist.",
      "Die Daten werden nur so lange gespeichert, wie es zur Bearbeitung Ihrer Anfrage notwendig ist oder gesetzliche Aufbewahrungsfristen dies erfordern.",
      "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie das Recht, Ihre Einwilligung jederzeit mit Wirkung für die Zukunft zu widerrufen.",
    ],
    controller: "Verantwortlicher",
    backHome: "← Zurück zur Startseite",
    updated: "Zuletzt aktualisiert",
  },
};

export default function Privacy({ lang: langProp }) {
  const { pathname } = useLocation();
  const lang = langProp || (pathname.startsWith("/de") ? "de" : "en");
  const t = I18N[lang];

  useSeo({
    title: lang === "de" ? "Datenschutzerklärung | MUConcierge" : "Privacy Policy | MUConcierge",
    description: lang === "de"
      ? "Datenschutzerklärung von MUConcierge — wie wir Ihre personenbezogenen Daten gemäß DSGVO verarbeiten."
      : "MUConcierge Privacy Policy — how we process your personal data in accordance with GDPR.",
    path: lang === "de" ? "/de/datenschutz" : "/privacy",
    lang,
    alternates: { en: "/privacy", de: "/de/datenschutz" },
  });

  const homePath = lang === "de" ? "/de" : "/";

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 sm:px-8 md:px-12 py-16 md:py-24" data-testid="privacy-page">
        <p className="tag-line text-[#C26D5C] mb-4">{t.overline}</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05] mb-3">
          {t.title}
        </h1>
        <h2 className="font-serif text-2xl text-[#5C635A] italic font-light mb-12">{t.subtitle}</h2>

        <p className="text-[#1A201C] leading-relaxed text-[15px] mb-8">{t.intro}</p>

        <ul className="space-y-4 text-[#1A201C] leading-relaxed text-[15px] mb-12">
          {t.bullets.map((b, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-[#C26D5C] mt-2 w-1 h-1 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <div className="bg-white border border-[#E5E2DC] p-8 mt-8">
          <p className="tag-line text-[#1C3326] mb-2">{t.controller}</p>
          <p className="text-[#1A201C] leading-relaxed text-[15px]">
            MUConcierge<br />
            Munich, Germany<br />
            <a href="mailto:hello@muconcierge.com" className="underline underline-offset-2 hover:text-[#C26D5C]">hello@muconcierge.com</a>
          </p>
        </div>

        <p className="text-xs text-[#5C635A] mt-10">
          {t.updated}: {new Date().toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { year: "numeric", month: "long", day: "numeric" })} ·{" "}
          <Link to={homePath} className="underline underline-offset-2 hover:text-[#1C3326]">{t.backHome}</Link>
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
